import { create } from "zustand";
import { chimeEngine } from "./pulseAudio";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Nexus Pipeline Live Pulse — realtime store.
 * Subscribes to SSE `/api/pulse/stream` (admin gated via query-string token).
 */
export const usePulseStore = create((set, get) => ({
  connected: false,
  latencyMs: 0,
  handshakes: 0,
  activeLeads: {},
  handoffQueue: [],
  streamHistory: [],
  stageCounts: {
    INTAKE: 0,
    AGENT_TRIAGE: 0,
    AI_VERIFY: 0,
    HUMAN_HANDOFF_READY: 0,
    LO_ENGAGED: 0,
    CLOSED: 0,
  },
  totals: { active: 0, closed: 0, handoffReady: 0 },
  unreadAlerts: [],
  seenLeadIds: new Set(),
  soundEnabled: true,
  wakeLockActive: false,
  audioVolume: 0.35,
  eventSource: null,
  wakeLock: null,

  applySnapshot: (snap, pingedAt) => {
    const prevSeen = get().seenLeadIds;
    const nextSeen = new Set(prevSeen);
    // Trigger chime for freshly-arrived handoff-ready leads (not present before)
    const newAlerts = [];
    for (const lead of snap.handoffQueue || []) {
      if (!nextSeen.has(lead.id)) {
        nextSeen.add(lead.id);
        newAlerts.push({
          id: `alert-${lead.id}-${Date.now()}`,
          leadId: lead.id,
          applicantName: lead.fullName,
          sector: lead.sector,
          dti: lead.dti,
          ltv: lead.ltv,
          triggeredAt: new Date().toISOString(),
          audioPlayed: false,
        });
      }
    }
    const enabled = get().soundEnabled;
    if (newAlerts.length && enabled) {
      chimeEngine.playHandoffChime(get().audioVolume);
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
        for (const a of newAlerts) {
          try {
            new Notification("Handoff Ready", {
              body: `${a.applicantName} · ${a.sector} · DTI ${a.dti}%`,
              silent: false,
            });
          } catch { /* ignore */ }
        }
      }
    }
    set((state) => ({
      activeLeads: snap.activeLeads || {},
      handoffQueue: snap.handoffQueue || [],
      streamHistory: snap.streamHistory || [],
      stageCounts: { ...state.stageCounts, ...(snap.stageCounts || {}) },
      totals: snap.totals || state.totals,
      connected: true,
      latencyMs: pingedAt ? Math.max(0, Date.now() - pingedAt) : state.latencyMs,
      unreadAlerts: newAlerts.length
        ? [...newAlerts, ...state.unreadAlerts].slice(0, 20)
        : state.unreadAlerts,
      seenLeadIds: nextSeen,
    }));
  },

  onHandoffEvent: (ev) => {
    set((s) => ({ handshakes: s.handshakes + 1 }));
  },

  acknowledgeHandoff: async (leadId) => {
    try {
      await fetch(`${BACKEND}/api/pulse/ack`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": localStorage.getItem("pulse_admin_token") || "",
        },
        body: JSON.stringify({ lead_id: leadId, lo_id: "lo-dwig" }),
      });
      set((s) => ({
        unreadAlerts: s.unreadAlerts.filter((a) => a.leadId !== leadId),
        handoffQueue: s.handoffQueue.filter((l) => l.id !== leadId),
      }));
    } catch (e) {
      console.error("ack failed", e);
    }
  },

  connect: async () => {
    if (typeof window === "undefined" || get().eventSource) return;
    const token = localStorage.getItem("pulse_admin_token");
    if (!token) return;
    // Use polling snapshot instead of EventSource (EventSource cannot send admin header)
    const pingedAt = Date.now();
    try {
      const res = await fetch(`${BACKEND}/api/pulse/snapshot`, {
        headers: { "X-Admin-Token": token },
      });
      if (res.ok) {
        const snap = await res.json();
        get().applySnapshot(snap, pingedAt);
      }
    } catch (e) {
      console.error("snapshot failed", e);
    }
    const timer = setInterval(async () => {
      const t = localStorage.getItem("pulse_admin_token");
      if (!t) return;
      const ping = Date.now();
      try {
        const res = await fetch(`${BACKEND}/api/pulse/snapshot`, {
          headers: { "X-Admin-Token": t },
        });
        if (res.ok) {
          const snap = await res.json();
          get().applySnapshot(snap, ping);
        }
      } catch (e) {
        set({ connected: false });
      }
    }, 2000);
    set({ eventSource: timer });
  },

  disconnect: () => {
    const es = get().eventSource;
    if (es) clearInterval(es);
    set({ eventSource: null, connected: false });
  },

  toggleSound: async () => {
    const enabled = !get().soundEnabled;
    set({ soundEnabled: enabled });
    if (enabled) {
      await chimeEngine.ensureActive();
      chimeEngine.playTick(0.18);
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch { /* ignore */ }
      }
    }
  },

  toggleWakeLock: async () => {
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;
    if (get().wakeLockActive && get().wakeLock) {
      try { await get().wakeLock.release(); } catch { /* ignore */ }
      set({ wakeLockActive: false, wakeLock: null });
      return;
    }
    try {
      const lock = await navigator.wakeLock.request("screen");
      lock.addEventListener?.("release", () => set({ wakeLockActive: false }));
      set({ wakeLockActive: true, wakeLock: lock });
    } catch (e) {
      console.warn("wake lock failed", e);
    }
  },

  seedDemoEvent: async () => {
    const token = localStorage.getItem("pulse_admin_token");
    if (!token) return;
    try {
      await fetch(`${BACKEND}/api/pulse/seed-demo-event`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
    } catch (e) { /* ignore */ }
  },
}));

/** Sub-store for the Lionesses Pride heartbeat panel. */
export const usePrideStore = create((set, get) => ({
  roles: [],
  events: [],
  running: false,

  refresh: async () => {
    const token = localStorage.getItem("pulse_admin_token");
    if (!token) return;
    try {
      const [stateRes, evRes] = await Promise.all([
        fetch(`${BACKEND}/api/pride/state`, { headers: { "X-Admin-Token": token } }),
        fetch(`${BACKEND}/api/pride/events?limit=25`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (stateRes.ok) set({ roles: (await stateRes.json()).roles || [] });
      if (evRes.ok) set({ events: (await evRes.json()).events || [] });
    } catch { /* ignore */ }
  },

  dispatch: async (eventType, payload = {}) => {
    const token = localStorage.getItem("pulse_admin_token");
    if (!token) return null;
    set({ running: true });
    try {
      const res = await fetch(`${BACKEND}/api/pride/dispatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify({ event_type: eventType, payload }),
      });
      const data = res.ok ? await res.json() : null;
      await get().refresh();
      return data;
    } finally {
      set({ running: false });
    }
  },
}));
